import { useState, useCallback, useRef, useEffect } from 'react';

import { refusalMessage } from '../../../lib/chatRefusal';
import {
  ChatMessage,
  SessionWithMessages,
  TaskStatus,
  SendMessageResponse,
  PersonaCited,
  StructuredProgress,
} from '../types';

const API_URL = import.meta.env.VITE_API_URL || '';
const POLL_INTERVAL = 2000;

export function useHealthcareChat(sessionId: number | null) {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [progress, setProgress] = useState<string>('');
  const [structuredProgress, setStructuredProgress] = useState<StructuredProgress | null>(null);
  const [error, setError] = useState<string | null>(null);

  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const loadSession = useCallback(async (id: number) => {
    try {
      setIsLoading(true);
      const response = await fetch(`${API_URL}/healthcare-chat-v2/sessions/${id}`, {
        credentials: 'include',
      });

      if (!response.ok) throw new Error('Failed to load session');

      const data: SessionWithMessages = await response.json();
      setMessages(data.messages || []);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load session');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (sessionId) {
      loadSession(sessionId);
    } else {
      setMessages([]);
    }

    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
      }
    };
  }, [sessionId, loadSession]);

  const pollTaskStatus = useCallback(async (taskId: string) => {
    try {
      const response = await fetch(`${API_URL}/healthcare-chat-v2/status/${taskId}`, {
        credentials: 'include',
      });

      if (!response.ok) throw new Error('Failed to get status');

      const status: TaskStatus = await response.json();

      if (status.state === 'PENDING' || status.state === 'PROGRESS') {
        setProgress(status.message || 'Processing...');
        // Set structured progress if available
        if (status.structured_progress) {
          const sp = status.structured_progress;
          console.log('[Progress] Stage:', sp.current_stage, '| Plan steps:', sp.plan_steps?.length ?? 0, '| Version:', sp.plan_version);
          if (sp.plan_steps && sp.plan_steps.length > 0) {
            console.log('[Progress] Plan steps:', sp.plan_steps.map((s: { label: string; status: string }) => `${s.status}: ${s.label}`));
          }
          setStructuredProgress(status.structured_progress);
        } else {
          console.log('[Progress] No structured_progress in response, state:', status.state);
        }
      } else if (status.state === 'SUCCESS' && status.result) {
        if (pollRef.current) {
          clearInterval(pollRef.current);
          pollRef.current = null;
        }

        // Handle personas_cited - could be array of ids or objects
        let personasCited: PersonaCited[] = [];
        if (status.result.personas_cited) {
          personasCited = status.result.personas_cited.map((p: PersonaCited | number) => {
            if (typeof p === 'number') {
              return { id: p };
            }
            return p;
          });
        }

        setMessages((prev) => [
          ...prev,
          {
            role: 'assistant',
            content: status.result!.response,
            metadata: {
              format: status.result!.format,
              personas_cited: personasCited,
              plan_review: status.result!.plan_review,
            },
          },
        ]);

        setIsSending(false);
        setProgress('');
        setStructuredProgress(null);
      } else if (status.state === 'FAILURE') {
        if (pollRef.current) {
          clearInterval(pollRef.current);
          pollRef.current = null;
        }

        setError(status.error || 'Request failed');
        setIsSending(false);
        setProgress('');
        setStructuredProgress(null);
      }
    } catch (err) {
      console.error('Poll error:', err);
    }
  }, []);

  const sendMessage = useCallback(
    async (query: string) => {
      if (!sessionId || !query.trim() || isSending) return;

      const userMessage: ChatMessage = {
        role: 'user',
        content: query.trim(),
        timestamp: new Date().toISOString(),
      };
      setMessages((prev) => [...prev, userMessage]);

      setIsSending(true);
      setProgress('Starting...');
      setStructuredProgress(null);  // Reset structured progress for new message
      setError(null);

      try {
        const response = await fetch(`${API_URL}/healthcare-chat-v2/sessions/${sessionId}/chat`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ query: query.trim() }),
        });

        if (!response.ok) {
          // A 403 here is the plan's chat cap refusing with a body that names it, not a login
          // problem. Six surfaces were fixed for this; this hook is the seventh, and it threw the
          // reason away — a user out of questions read "Failed to send message" and had nothing to
          // act on. 401 is the genuinely-unauthenticated case and keeps its own wording.
          if (response.status === 403) {
            setError(await refusalMessage(response));
            setIsSending(false);
            setProgress('');
            return;
          }
          if (response.status === 401) {
            setError('Your session has expired. Please sign in again.');
            setIsSending(false);
            setProgress('');
            return;
          }
          throw new Error('Failed to send message');
        }

        const data: SendMessageResponse = await response.json();

        pollRef.current = setInterval(() => {
          pollTaskStatus(data.task_id);
        }, POLL_INTERVAL);

        pollTaskStatus(data.task_id);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to send message');
        setIsSending(false);
        setProgress('');
      }
    },
    [sessionId, isSending, pollTaskStatus]
  );

  return {
    messages,
    isLoading,
    isSending,
    progress,
    structuredProgress,
    error,
    sendMessage,
    loadSession,
  };
}
